import { useEffect, useState } from 'react';
import { FieldPath, FieldValues, Path, PathValue, get, useFormState } from 'react-hook-form';
import { FormFieldProps, SelectField, TextInputField } from '@brighte/form-components';
import { AddressState, AddressType, nameof, requiredFieldError } from '@brighte/validation';
import { Columns } from '@spark-web/columns';
import { Field } from '@spark-web/field';
import { Stack } from '@spark-web/stack';
import { TextInput } from '@spark-web/text-input';
import { useAddressDetails } from '../../hooks';
import { LABELS, formatAddress } from '../../utils';
import AutocompleteAddress from './AddressAutocomplete/AddressAutocomplete';

const { POSTCODE, STATE, STREET_NAME, STREET_NUMBER, SUBURB, UNIT_NUMBER, MANUAL_ADDRESS } = LABELS;

type Props<TFieldValues extends FieldValues> = {
  defaultValue?: AddressType | null;
  disabled?: boolean;
} & FormFieldProps<TFieldValues>;

function getStateList() {
  return Object.values(AddressState).map(state => ({
    label: state.toString(),
    value: state,
  }));
}

function Address<TFieldValues extends FieldValues = FieldValues>({
  label,
  name,
  control,
  setValue,
  defaultValue = null,
  disabled = false,
}: Props<TFieldValues>) {
  const [addressState, setAddressState] = useState<{ address: string | null; showManual: boolean }>({
    address: null,
    showManual: false,
  });

  const [defaultAddress, setDefaultAddress] = useState(defaultValue);
  const { errors } = useFormState({ control });
  const fieldError = get(errors, name);

  const setFieldValue = (value: Partial<AddressType> | null) => {
    if (value != null) {
      const fullAddress = formatAddress(value as AddressType);
      setValue?.(name, { fullAddress, ...value } as PathValue<TFieldValues, Path<TFieldValues>>, {
        shouldValidate: true,
      });
    }
  };

  const handleAddressChange = (value: string | null) => {
    if (value === MANUAL_ADDRESS) {
      setAddressState({ address: value, showManual: true });
    } else {
      setAddressState({ address: value, showManual: false });
    }
  };

  //Remove the default address whenever there is a change address field.
  const handleDefaultAddressChange = () => {
    setDefaultAddress(null);
  };

  const { isError: isAddressError, error: addressError } = useAddressDetails(addressState.address, {
    onSuccess: setFieldValue,
  });

  useEffect(() => {
    setDefaultAddress(defaultValue);
    setFieldValue(defaultValue);
  }, [defaultValue]);

  // Return a disabled TextField component with the pre-filled address
  if (defaultAddress) {
    return (
      <Field disabled={disabled} label={label}>
        <TextInput value={formatAddress(defaultAddress)} onChange={handleDefaultAddressChange} />
      </Field>
    );
  }

  return (
    <Stack gap="large">
      {isAddressError ? <div>{addressError}</div> : null}
      <AutocompleteAddress
        label={label}
        onChange={handleAddressChange}
        tone="critical"
        message={fieldError && requiredFieldError(label)}
        defaultOption={{ option: { value: LABELS.MANUAL_ADDRESS } }}
      />
      {addressState.showManual ? (
        <>
          <Columns gap="large">
            <TextInputField
              name={`${name}.${nameof<AddressType>('unitNumber')}` as FieldPath<TFieldValues>}
              label={UNIT_NUMBER}
              control={control}
              FieldProps={{ secondaryLabel: '(Optional)' }}
            />
            <TextInputField
              name={`${name}.${nameof<AddressType>('streetNumber')}` as FieldPath<TFieldValues>}
              label={STREET_NUMBER}
              control={control}
            />
            <TextInputField
              name={`${name}.${nameof<AddressType>('streetName')}` as FieldPath<TFieldValues>}
              label={STREET_NAME}
              control={control}
            />
          </Columns>
          <Columns gap="large">
            <TextInputField
              name={`${name}.${nameof<AddressType>('suburb')}` as FieldPath<TFieldValues>}
              label={SUBURB}
              control={control}
            />
            <SelectField
              name={`${name}.${nameof<AddressType>('state')}` as FieldPath<TFieldValues>}
              placeholder="Please select a State"
              label={STATE}
              control={control}
              options={getStateList()}
            />
            <TextInputField
              name={`${name}.${nameof<AddressType>('postcode')}` as FieldPath<TFieldValues>}
              label={POSTCODE}
              control={control}
            />
          </Columns>
        </>
      ) : null}
    </Stack>
  );
}

export { Address };
